import { Search } from "lucide-react";
import { useRef, useState } from "react";

import DaumPostcodeDialog from "@/components/daum-postcode-dialog";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { AddressPick } from "@/lib/daum-postcode";
import { ADDRESS_DETAIL_MAX, AddressGroup, composeSiteAddress, EMPTY_ADDRESS } from "@buildpy/core/modules/customer/address";

export interface Picked {
  road: string | null;
  jibun: string | null;
  zonecode: string | null;
  bcode: string | null;
  buildingCode: string | null;
}

interface AddressFieldProps {
  // 손 입력칸·상세칸이 받는 id — 바깥 label 의 htmlFor 와 맞춘다.
  id?: string;
  label?: string;
  initial?: Partial<AddressGroup>;
  inputSize?: "default" | "lg";
  placeholder?: string;
  onChange?: (value: AddressGroup) => void;
}

const pickedOf = (initial: Partial<AddressGroup>): Picked | null =>
  initial.address_road || initial.address_jibun
    ? {
        road: initial.address_road ?? null,
        jibun: initial.address_jibun ?? null,
        zonecode: initial.address_zonecode ?? null,
        bcode: initial.address_bcode ?? null,
        buildingCode: initial.address_building_code ?? null,
      }
    : null;

export const valueOf = (picked: Picked | null, typed: string, detail: string): AddressGroup => {
  if (!picked) return { ...EMPTY_ADDRESS, site_address: typed };
  const trimmed = detail.trim() || null;
  return {
    site_address: composeSiteAddress(picked.road ?? picked.jibun ?? "", trimmed),
    address_road: picked.road,
    address_jibun: picked.jibun,
    address_zonecode: picked.zonecode,
    address_bcode: picked.bcode,
    address_building_code: picked.buildingCode,
    address_detail: trimmed,
  };
};

export interface AddressFieldState {
  picked: Picked | null;
  typed: string;
  detail: string;
}

// 검색 결과를 고르면 코드만 갈고 쳐 둔 상세는 남긴다 · 도로명·지번이 둘 다 비면 고른 것이 없는 것이라 그대로 돌려준다.
export const applyPick = (state: AddressFieldState, pick: AddressPick): AddressFieldState => {
  const picked: Picked = {
    road: pick.road || null,
    jibun: pick.jibun || null,
    zonecode: pick.zonecode || null,
    bcode: pick.bcode || null,
    buildingCode: pick.buildingCode || null,
  };
  return picked.road || picked.jibun ? { ...state, picked } : state;
};

// 직접 입력으로 바꾸면 지금 보이던 주소 글자를 손 입력칸에 옮기고 코드·상세는 비운다.
export const toManualState = (state: AddressFieldState): AddressFieldState => ({
  picked: null,
  typed: valueOf(state.picked, state.typed, state.detail).site_address,
  detail: "",
});

// 현장 주소 한 칸 — 검색으로 고르면 코드를 숨은 칸으로 같이 보내고, 손으로 치면 글자만 보낸다(`readAddressGroup`).
const AddressField = ({
  id = "site_address",
  label,
  initial = {},
  inputSize = "default",
  placeholder,
  onChange,
}: AddressFieldProps) => {
  const [state, setState] = useState<AddressFieldState>(() => ({
    picked: pickedOf(initial),
    typed: initial.site_address ?? "",
    detail: initial.address_detail ?? "",
  }));
  const { picked, typed, detail } = state;
  const [open, setOpen] = useState(false);
  const detailRef = useRef<HTMLInputElement>(null);
  const focusDetail = useRef(false);

  const value = valueOf(picked, typed, detail);
  const commit = (next: AddressFieldState) => {
    setState(next);
    onChange?.(valueOf(next.picked, next.typed, next.detail));
  };
  const buttonSize = inputSize === "lg" ? "lg" : "default";

  const complete = (pick: AddressPick) => {
    const next = applyPick(state, pick);
    setOpen(false);
    if (next === state) return;
    focusDetail.current = true;
    commit(next);
  };

  const searchButton = (text: string) => (
    <Button className="shrink-0" size={buttonSize} type="button" variant="outline" onClick={() => setOpen(true)}>
      <Search className="size-4" />
      {text}
    </Button>
  );

  return (
    <>
      {picked ? (
        <div className="flex w-full flex-col gap-1.5">
          {label && <Label htmlFor={id}>{label}</Label>}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted px-3 py-2">
            <div className="min-w-0 flex-1 text-sm">
              <p className="break-keep font-medium text-foreground">
                {picked.zonecode && <span className="mr-1 text-muted-foreground">({picked.zonecode})</span>}
                {picked.road ?? picked.jibun}
              </p>
              {picked.road && picked.jibun && <p className="break-keep text-xs text-muted-foreground">지번 {picked.jibun}</p>}
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="sm" type="button" variant="ghost" onClick={() => setOpen(true)}>
                다시 검색
              </Button>
              <Button size="sm" type="button" variant="ghost" onClick={() => commit(toManualState(state))}>
                직접 입력
              </Button>
            </div>
          </div>
          <Input
            ref={detailRef}
            aria-label="상세주소"
            id={id}
            inputSize={inputSize}
            maxLength={ADDRESS_DETAIL_MAX}
            name="address_detail"
            placeholder="상세주소 (동·호수)"
            value={detail}
            onChange={(event) => commit({ ...state, detail: event.target.value })}
          />
          <input name="site_address" type="hidden" value={value.site_address} />
          <input name="address_road" type="hidden" value={value.address_road ?? ""} />
          <input name="address_jibun" type="hidden" value={value.address_jibun ?? ""} />
          <input name="address_zonecode" type="hidden" value={value.address_zonecode ?? ""} />
          <input name="address_bcode" type="hidden" value={value.address_bcode ?? ""} />
          <input name="address_building_code" type="hidden" value={value.address_building_code ?? ""} />
        </div>
      ) : (
        <Input
          buttonSection={() => searchButton("주소 검색")}
          id={id}
          inputSize={inputSize}
          label={label}
          maxLength={200}
          name="site_address"
          placeholder={placeholder}
          value={typed}
          onChange={(event) => commit({ ...state, typed: event.target.value })}
        />
      )}
      <DaumPostcodeDialog
        open={open}
        onCloseAutoFocus={(event) => {
          if (!focusDetail.current) return;
          focusDetail.current = false;
          event.preventDefault();
          detailRef.current?.focus();
        }}
        onComplete={complete}
        onOpenChange={setOpen}
      />
    </>
  );
};

export default AddressField;
